import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Input, Button, Typography, theme } from 'antd';
import { AnimatePresence, motion, useDragControls } from 'framer-motion';
import { IconCalendarOff } from '@tabler/icons-react';
import { message } from '../services/toastMessage';
import { useTranslation } from 'react-i18next';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';
import { hostCancelBooking } from '../services/BookingService';
import { IBooking } from '../interfaces/heavyDutyMachinery/IBooking';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const { TextArea } = Input;
const { Text } = Typography;

interface HostCancelBookingModalProps {
  bookingId: string;
  open: boolean;
  onClose: () => void;
  onCancelled: (booking: IBooking) => void;
}

/**
 * Host-side "cancel this booking, but explain why first" action. Desktop
 * keeps a plain antd Modal (same required-reason shape as
 * RespondToReviewModal/AdminReasonModal). Mobile instead gets the app's own
 * creamy bottom-sheet language -- same portal/backdrop/drag-to-dismiss shape
 * as ConfirmBottomSheet (see its own doc comment for why a sheet reads
 * better than a small centered dialog on a phone) -- so this doesn't read as
 * a bare antd dialog dropped into an otherwise pill-and-Airbnb-styled app.
 * The reason is what the guest ends up seeing (see BookingCancellationHelper),
 * so it's written to be read by them, not logged for internal record-keeping only.
 */
const HostCancelBookingModal: React.FC<HostCancelBookingModalProps> = ({ bookingId, open, onClose, onCancelled }) => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { token } = theme.useToken();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Drag only starts from the grab handle (see its onPointerDown below), not
  // from anywhere on the sheet -- unlike ConfirmBottomSheet, this sheet holds
  // a TextArea the guest needs to tap/scroll/select text inside, which a
  // whole-sheet drag listener would fight with.
  const dragControls = useDragControls();

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_HOST_CANCEL_REASON_REQUIRED));
      return;
    }
    setSubmitting(true);
    try {
      const booking = await hostCancelBooking(bookingId, reason.trim());
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_HOST_CANCEL_SUCCESS));
      onCancelled(booking);
      handleClose();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_HOST_CANCEL_ERROR));
    } finally {
      setSubmitting(false);
    }
  };

  if (isMobile) {
    return createPortal(
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="host-cancel-sheet-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleClose}
              style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.45)' }}
            />
            <motion.div
              key="host-cancel-sheet"
              drag="y"
              dragControls={dragControls}
              dragListener={false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.5 }}
              dragSnapToOrigin
              onDragEnd={(_, info) => {
                if (info.offset.y > 80 || info.velocity.y > 500) handleClose();
              }}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              style={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2101,
                width: '100%',
                maxWidth: 480,
                margin: '0 auto',
                background: token.colorBgContainer,
                borderRadius: '20px 20px 0 0',
                boxShadow: '0 -8px 24px rgba(0,0,0,0.16)',
                padding: '10px 20px calc(20px + env(safe-area-inset-bottom, 0px))',
              }}
            >
              <div
                onPointerDown={(e) => dragControls.start(e)}
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  background: token.colorBorder,
                  margin: '0 auto 16px',
                  cursor: 'grab',
                  touchAction: 'none',
                }}
              />
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
                <span
                  style={{
                    display: 'flex',
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: token.colorErrorBg,
                    color: token.colorError,
                  }}
                >
                  <IconCalendarOff size={20} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <Text strong style={{ display: 'block', fontSize: 16 }}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_HOST_CANCEL_MODAL_TITLE)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_HOST_CANCEL_LABEL)}
                  </Text>
                </div>
              </div>
              <TextArea
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_HOST_CANCEL_PLACEHOLDER)}
                maxLength={1000}
                style={{ borderRadius: 16, marginBottom: 16 }}
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <Button
                  block
                  style={{ borderRadius: 999, border: `1px solid ${token.colorBorder}`, background: 'transparent' }}
                  onClick={handleClose}
                >
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SHEET_KEEP)}
                </Button>
                <Button
                  block
                  loading={submitting}
                  onClick={handleSubmit}
                  style={{ borderRadius: 999, background: token.colorError, borderColor: token.colorError, color: '#fff' }}
                >
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_HOST_CANCEL_CONFIRM_BUTTON)}
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );
  }

  return (
    <Modal
      title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_HOST_CANCEL_MODAL_TITLE)}
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_HOST_CANCEL_CONFIRM_BUTTON)}
      okButtonProps={{ danger: true }}
      confirmLoading={submitting}
    >
      <div style={{ marginBottom: 8 }}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_HOST_CANCEL_LABEL)}</div>
      <TextArea
        rows={4}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_HOST_CANCEL_PLACEHOLDER)}
        maxLength={1000}
      />
    </Modal>
  );
};

export default HostCancelBookingModal;
